import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { Options, Requisition } from 'models'

import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'

import styles from '../Dialog.module.sass'

import useSubmitTask from 'hooks/useSubmitTask'
import {
	ClinicalAppointmentApprovalSchema,
	ApproveClinicalAppointmentSchema,
	ExtendedAppointmentApprovalSchema,
} from './ClinicalAppointmentApprovalSchema'
import { ClinicalAppointmentForm } from './ClinicalAppointmentForm'
import useSession from 'hooks/useSession'
import toBool from 'utils/toBool'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import TaskNotesController from 'controllers/TaskManager/TaskSubControllers/TaskNotesController/TaskNotesController'
import AlertBox from 'components/AlertBox/AlertBox'
import InfoList from 'components/InfoList/InfoList'
import { mapClinicalAppointmentApprovalDetails } from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import { useNoWaitFetchList } from 'store'
import TaskRefusalController from 'controllers/TaskManager/TaskSubControllers/TaskRefusalController/TaskRefusalController'
import PersonalAccidentWarning from 'controllers/TaskManager/TaskSubControllers/PersonalAccidentWarning'
import { RequisitionType } from 'utils/enums'

const ClinicalAppointmentApprovalTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const [defaultValues, setDefaultValues] = useState(null)
	const [argValues, setArgValues] = useState(null)
	const [submissionType, setSubmissionType] = useState(null)
	const history = useHistory()
	const { user: sessionUser } = useSession()

	const IS_ADMINISTRATIVE = sessionUser?.roles.includes('manager')

	const schema = IS_ADMINISTRATIVE
		? submissionType === 'approve'
			? ApproveClinicalAppointmentSchema({ ...argValues })
			: ExtendedAppointmentApprovalSchema({ ...argValues })
		: ClinicalAppointmentApprovalSchema({ ...argValues })

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(schema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, watch, getValues, reset, setValue } =
		methods

	const [outsideNetwork] = watch(['outsideNetwork'])

	const { contents } = useNoWaitFetchList(
		[
			`clinical_processes/${data?.processId}/requisitions/${data?.requisitionId}/notes`,
			`clinical_processes/${data.processId}/requisitions/${data.requisitionId}`,
			outsideNetwork && `metadata/districts`,
			IS_ADMINISTRATIVE && !outsideNetwork
				? argValues?.specialtyId
					? `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/specialties/${argValues.specialtyId}/service_providers`
					: `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/service_providers`
				: null,
		],
		[
			(json) => {
				return json.notes
			},
			Requisition.fromJSON,
			IS_ADMINISTRATIVE && outsideNetwork && Options.fromSimpleObjectJSON,
			IS_ADMINISTRATIVE &&
				!outsideNetwork &&
				Options.fromSimpleObjectJSON,
		]
	)

	const [
		notesContents,
		requisitionContents,
		districtsContents,
		serviceProvidersContents,
	] = contents

	const { contents: notes, state: notesState } =
		notesContents && notesContents.state === 'hasValue'
			? notesContents
			: { state: notesContents?.state }

	const { contents: requisition, state: requisitionState } =
		requisitionContents && requisitionContents.state === 'hasValue'
			? requisitionContents
			: { state: requisitionContents?.state }

	const { contents: districts } =
		districtsContents && districtsContents.state === 'hasValue'
			? districtsContents
			: { state: districtsContents?.state }

	const { contents: serviceProviders } =
		serviceProvidersContents &&
		serviceProvidersContents.state === 'hasValue'
			? serviceProvidersContents
			: { state: serviceProvidersContents?.state }

	const isLoading = requisitionState === 'loading'
	const isNotesLoading = notesState === 'loading'

	const {
		error,
		submitting,
		resolveTask,
		approveTask,
		requestApprovalTask,
		requestManagerApprovalTask,
	} = useSubmitTask(data?.id, formState?.errors)

	useEffect(() => {
		if (
			!isLoading &&
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(data.formState ? data.formState : defaultValues)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		if (!isLoading) {
			let defaultValues = {}
			if (requisition) {
				setArgValues({
					requestEscort: requisition?.requestEscort,
					requiresChargeAmount: data?.requiresChargeAmount,
					specialtyId: requisition?.specialty?.id,
				})
			}

			if (requisition?.requestEscort && IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					'needsOptions.housing': false,
					'needsOptions.transport': false,
				}
			}
			if (IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					notes: requisition?.notes || '',
					outsideNetwork: toBool(
						requisition?.establishment?.outsideNetwork
					),
					administrativeObservation:
						requisition?.administrativeObservation || '',
				}
			}

			defaultValues = {
				...defaultValues,
				needEscort: false,
			}

			if (serviceProviders) {
				const establishmentId =
					requisition?.establishment?.id.toString()
				const hasEstablishment = serviceProviders.some(
					(provider) => provider.value === establishmentId
				)

				if (hasEstablishment) {
					defaultValues = {
						providerId: establishmentId,
						...defaultValues,
					}
				}
			}
			setDefaultValues({ ...defaultValues })
		}
	}, [isLoading, requisition ?? {}, serviceProviders ?? []])

	const onSubmit = (formData) => {
		const hasFetchedConnection = getValues('hasFetchedConnection')
		const decidirMaisTarde = getValues('decidirMaisTarde')
		const transferAppointments = getValues('transferAppointments')

		if (
			props.data?.requisitionType?.id === RequisitionType.Appointment &&
			hasFetchedConnection &&
			!decidirMaisTarde &&
			!(transferAppointments?.length > 0)
		) {
			setValue('connectionError', true)
			return
		}

		const submitData = {
			...formData,
			transferAppointments,
			decidirMaisTarde,
		}

		if (submissionType === 'resolve') {
			resolveTask(submitData)
				.then(() => resolve())
				.catch(() => {})
		} else if (submissionType === 'approve') {
			approveTask(submitData)
				.then(() => resolve())
				.catch(() => {})
		}
	}

	if (isLoading && !formState.isDirty) {
		return <LoadingIndicator />
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	const details = mapClinicalAppointmentApprovalDetails(requisition, i18n)

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
					submissionType={submissionType}
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController
						task={data}
						requisition={requisition}
					/>

					<div className={styles['notes-form-container']}>
						<TaskNotesController
							data={{
								id: data.id,
								processId: data.processId,
								taskNote: notes,
							}}
							requisition={requisition}
							isNotesLoading={isNotesLoading}
						/>
					</div>

					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}

						<TaskRefusalController requisition={requisition} />

						<PersonalAccidentWarning task={data} />

						{IS_ADMINISTRATIVE && (
							<InfoList
								items={[
									{
										title: i18n`Medical observation`,
										text:
											requisition?.clinicalObservation ||
											'-',
										icon: 'observation',
									},
								]}
							/>
						)}

						<InfoList items={details} />

						<ClinicalAppointmentForm
							data={{
								requisition,
								requiresChargeAmount:
									data?.requiresChargeAmount,
								isAdministrative: IS_ADMINISTRATIVE,
								outsideNetwork: outsideNetwork,
								districts: districts,
								serviceProviders: serviceProviders,
								isFromIsland: data?.injured?.isFromIsland,
								processId: data?.processId,
								appointmentId: data?.appointmentId,
								requisitionType: data?.requisitionType?.id,
								requisitionStateId: data?.requisitionState?.id,
								requisitionId: data?.requisitionId,
							}}
						/>
					</form>
				</div>
			</FormProvider>
			{IS_ADMINISTRATIVE ? (
				<TaskMenuController
					data={{
						formId: id,
						options: ['postpone', 'forward', 'refuse'],
						submitting,
						setSubmissionType,
						submissionType: 'resolve',
						requestApprovalTask,
						requestManagerApprovalTask,
						getValues,
					}}
				/>
			) : (
				<TaskMenuController
					data={{
						formId: id,
						options: ['forward', 'refuse'],
						history,
						submitting,
						setSubmissionType,
						submissionType: 'approve',
						getValues,
					}}
				/>
			)}
		</>
	)
}

export default ClinicalAppointmentApprovalTaskController
